import { Box, Grid, makeStyles } from '@material-ui/core';
import {
  colors,
  DefaultButton,
  DefaultCard,
  MessageBox,
  PhotoCard,
  TextField,
  useFormHook,
} from 'design-system';
import { useMemo } from 'react';
// funtions
import { isAlphaNumeric } from 'src/functions/isAlphaNumeric';
// models
import { EmissionResume } from 'src/models/emission/EmissionResume';
import { VinChangeData } from 'src/models/emission/VinChangeData';
// redux
import { connect, ConnectedProps } from 'react-redux';
import {
  updateEmissionPolicyVin,
  updateVinFormValues,
} from 'src/store/emission/actions';
import { RootState } from 'src/store/rootReducer';
// form helpers
import { makeInitialValues } from './formHelper';
import validationSchema from './validationSchema';

const useStyles = makeStyles(() => ({
  updateButton: {
    padding: '20px 20px',
    backgroundColor: colors.onahau,
    width: '100%',
    position: 'sticky',
    bottom: 0,
  },
  form: {
    display: 'flex',
    flexGrow: 1,
    flexFlow: 'column',
  },
  formContent: {
    display: 'flex',
    flexGrow: 1,
    overflowY: 'auto',
  },
  message: {
    lineHeight: '20px',
    letterSpacing: '0.3px',
    fontSize: 13,
    fontWeight: 400,
    paddingRight: '20px',
  },
  title: {
    fontWeight: 700,
  },
}));

export const VinModificationForm = ({
  emission,
  updateValuesAction,
  updatePolicyVinAction,
}: Props): JSX.Element => {
  const classes = useStyles();

  const handleFormSubmit = (data: VinChangeData): void => {
    updateValuesAction(data);
    updatePolicyVinAction();
  };

  const { getInputProps, handleSubmit, errors } = useFormHook<VinChangeData>(
    makeInitialValues(emission),
    handleFormSubmit,
    validationSchema,
  );

  const handleChangeInvoice = (name: string, file: string) => {
    getInputProps('invoiceName').onChange(name);
    getInputProps('invoiceBase64').onChange(file);
  };

  const disableSubmit = useMemo(() => {
    const existformErrors = Boolean(Object.keys(errors).length);
    const noVinChanged =
      emission.vinNumber === (getInputProps('vinNumber').value as string);
    const noInvoiceChanged =
      emission.invoiceBase64 ===
      (getInputProps('invoiceBase64').value as string);
    return existformErrors || noVinChanged || noInvoiceChanged;
  }, [emission, errors, getInputProps]);

  return (
    <form
      onSubmit={handleSubmit}
      className={classes.form}
      data-testid="vin-modification-form"
    >
      <Box className={classes.formContent}>
        <Box sx={{ width: '100%', px: 2, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MessageBox
                message={
                  <div className={classes.message}>
                    <span className={classes.title}>Cambios a la póliza:</span>{' '}
                    Puedes modidificar los datos del auto en caso de que hayan
                    cambiado recientemente. La póliza será generada nuevamente.
                  </div>
                }
                data-testid="vin-modification-message"
                variant="info"
                hideCloseButton
              />
            </Grid>
            <Grid item xs={12}>
              <DefaultCard
                id="vin-change-vincard"
                data-testid="vinCard"
                title="VIN o número de Chasis"
              >
                <TextField
                  inputId="vin-change-input-vin"
                  inputLabel="VIN"
                  inputName="vinNumber"
                  defaultValue={getInputProps('vinNumber').value as string}
                  inputError={getInputProps('vinNumber').error}
                  inputErrorMessage={getInputProps('vinNumber').errorText}
                  onChange={(val): void =>
                    getInputProps('vinNumber').onChange(val.toUpperCase())
                  }
                  shouldUpdate={isAlphaNumeric}
                />
              </DefaultCard>
            </Grid>
            <Grid item xs={12}>
              <PhotoCard
                id="vin-change-invoice"
                data-testid="vin-change-invoice-card"
                title="Factura de compra del auto"
                subTitle="Incluye una foto o el documento de la factura de tu auto nuevo."
                fileName={getInputProps('invoiceName').value as string}
                base64String={getInputProps('invoiceBase64').value as string}
                onLocalImageCaptured={handleChangeInvoice}
                confirmationMessage="¿Estás seguro que deseas eliminar el documento?"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box className={classes.updateButton}>
        <DefaultButton
          id="vin-modification-submit"
          fullWidth
          type="submit"
          disabled={disableSubmit}
          data-testid="vin-modification-submit"
        >
          Actualizar
        </DefaultButton>
      </Box>
    </form>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  emission: EmissionResume;
} => ({
  emission: state.emission.emission,
});

const reduxActions = {
  updateValuesAction: updateVinFormValues,
  updatePolicyVinAction: updateEmissionPolicyVin,
};

const connector = connect(mapStateToProps, reduxActions);

type Props = ConnectedProps<typeof connector>;

export default connector(VinModificationForm);
